import EmptyState from "@/components/EmptyState";
import Table, {
  TableFooter,
  TableControls,
  toTableControlActions,
  buildAvatarDetailsColumn,
  buildCopyTextColumn,
  buildActionColumn,
  buildExpandableCountColumn,
  buildTagColumn
} from "@/components/Table";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import type { Action } from "@/components/MenuButton";
import { useQuery } from "@apollo/client";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation, generatePath } from "react-router-dom";
import { Path } from "@/router";
import { useIsCompactLayout } from "@/hooks/useIsCompactLayout";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import SearchInput from "@/components/SearchInput";
import PersonSideSheet from "@/features/person/components/PersonSideSheet/PersonSideSheet";
import NotesTable from "@/features/global/components/NotesTable";
import AddEditNoteSideSheet from "@/features/global/components/AddEditNoteSideSheet";
import useCapabilities, { useCanManageFacilities } from "@/hooks/useCapabilities";
import { NoteNotableValues } from "@/types";
import { FACILITY_GOVERNANCE_ROLES_QUERY } from "./queries";
import { useGovernanceRoleArchival } from "./useGovernanceRoleArchival";
import { EditGovernanceRoleModal } from "./_EditGovernanceRoleModal";
import { AddGovernanceRoleModal } from "./_AddGovernanceRoleModal";
import { ArchiveGovernanceRoleDialog } from "./_ArchiveGovernanceRoleDialog";
import {
  getGovernanceStatusLabel,
  getGovernanceStatusTagType,
  getGovernanceStatusTagStyle,
  mapGovernanceStatus,
  matchesGovernanceSearch,
  matchesGovernanceRoleFilter,
  buildGovernanceRoleFilterOptions,
  getEffectiveGovernanceSearch,
  clearGovernanceFilters
} from "./_utils";
import { GovernanceRoleFilter } from "./_GovernanceRoleFilter";
import { useGovernanceRoleFilter } from "./useGovernanceRoleFilter";
import type { FacilityGovernanceRole } from "./types";
import { GovernanceStatus } from "./types";

export function FacilityGovernanceTable({ facilityId }: { facilityId: string }) {
  const { params, addSearchParam, deleteSearchParams } = useParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const { selectedRoleIds, onSelect: onSelectRole, onClear: onClearRole } = useGovernanceRoleFilter();
  const urlShowArchived = params.showArchived === "true";
  const [showArchived, setShowArchivedState] = useState(urlShowArchived);

  const setShowArchived = (value: boolean) => {
    setShowArchivedState(value);
    if (value) {
      addSearchParam("showArchived", "true");
    } else {
      deleteSearchParams("showArchived");
    }
  };

  const { data, loading } = useQuery(FACILITY_GOVERNANCE_ROLES_QUERY, {
    variables: { facilityId }
  });

  const { archiveRole, unarchiveRole, archiving } = useGovernanceRoleArchival(FACILITY_GOVERNANCE_ROLES_QUERY);

  const navigate = useNavigate();
  const location = useLocation();
  const { id: currentUserId } = useCapabilities();
  const canManageFacilities = useCanManageFacilities();
  const canManageGovernance = canManageFacilities;
  const { Menu, setMenu } = useTableRowMenu();
  const { isCompactLayout } = useIsCompactLayout();
  const [isPersonSideSheetOpen, setIsPersonSideSheetOpen] = useState(false);
  const [selectedPgaId, setSelectedPgaId] = useState<string | undefined>(undefined);
  const [roleToEdit, setRoleToEdit] = useState<FacilityGovernanceRole | null>(null);
  const [roleToArchive, setRoleToArchive] = useState<FacilityGovernanceRole | null>(null);
  const [isAddGovernanceRoleModalOpen, setIsAddGovernanceRoleModalOpen] = useState(false);
  const [roleToAddNote, setRoleToAddNote] = useState<FacilityGovernanceRole | null>(null);

  const allRoles = useMemo<FacilityGovernanceRole[]>(
    () =>
      (data?.facility.governanceRoles ?? []).map((node) => ({
        ...node,
        status: mapGovernanceStatus(node.status)
      })),
    [data]
  );

  const wasAllArchived = useRef<boolean | null>(null);
  useEffect(() => {
    if (loading || !data) {
      return;
    }

    const allArchived = allRoles.length > 0 && allRoles.every((role) => role.status === GovernanceStatus.ARCHIVED);
    const previouslyAllArchived = wasAllArchived.current;
    wasAllArchived.current = allArchived;

    if (allArchived && previouslyAllArchived !== true) {
      setShowArchivedState(true);
    }
  }, [loading, data, allRoles]);

  const effectiveSearch = getEffectiveGovernanceSearch(searchValue, debouncedSearch, params.search);

  const roles = useMemo(
    () =>
      allRoles
        .filter((role) => showArchived || role.status === GovernanceStatus.ACTIVE)
        .filter((role) => matchesGovernanceSearch(role, effectiveSearch))
        .filter((role) => matchesGovernanceRoleFilter(role, selectedRoleIds)),
    [allRoles, showArchived, effectiveSearch, selectedRoleIds]
  );

  const roleFilterOptions = useMemo(() => buildGovernanceRoleFilterOptions(allRoles), [allRoles]);

  const activeFilterCount = [showArchived, effectiveSearch, selectedRoleIds.length > 0].filter(Boolean).length;

  const hasActiveFilters = activeFilterCount > 0 || roles.length < allRoles.length;

  function handleClearAllFilters() {
    clearGovernanceFilters({
      setShowArchived: setShowArchivedState,
      setSearchValue,
      deleteSearchParams
    });
  }

  async function handleArchiveGovernanceRole() {
    if (!roleToArchive) {
      return;
    }

    const success = await archiveRole(roleToArchive.id);
    if (success) {
      setRoleToArchive(null);
    }
  }

  async function handleUnarchiveGovernanceRole(role: FacilityGovernanceRole) {
    await unarchiveRole(role.id);
  }

  function buildPersonPath(pgaId: string | null | undefined): Path | null {
    if (!pgaId) {
      return null;
    }
    const returnTo = location.pathname;
    return generatePath(`/people/:pgaId/person/overview?return_to=${encodeURIComponent(returnTo)}`, { pgaId }) as Path;
  }

  const columns = [
    ...buildAvatarDetailsColumn<FacilityGovernanceRole>({
      isCompactLayout,
      getTitle: (row) => row.person.displayName,
      getSubtitle: (row) => row.person.latestProgram?.programType?.name || "",
      subtitleLabel: "Program",
      getPicture: (row) => row.person.profilePhoto,
      header: "Name",
      size: 230
    }),
    buildCopyTextColumn<FacilityGovernanceRole>({
      accessorKey: "person.pgaId",
      header: "PGA ID",
      copyFieldLabel: "PGA ID",
      getText: (row) => row.person.pgaId || "",
      shouldShowCopyButton: (row) => !!row.person.pgaId,
      enableSorting: false
    }),
    {
      accessorFn: (row: FacilityGovernanceRole) => row.title.title,
      header: "Role",
      id: "role",
      enableSorting: false
    },
    buildExpandableCountColumn<FacilityGovernanceRole>({
      accessorKey: "notes",
      header: "Notes",
      getCount: (row) => row.notes.length,
      size: 100,
      minSize: 80,
      showExpandIcon: true
    }),
    buildTagColumn<FacilityGovernanceRole>({
      accessorKey: "status",
      header: "Status",
      getTagLabel: (row) => getGovernanceStatusLabel(row.status),
      getTagType: (row) => getGovernanceStatusTagType(row.status),
      getTagStyle: (row) => getGovernanceStatusTagStyle(row.status),
      meta: {
        headerClassName: "hide-header-in-column"
      }
    }),
    buildActionColumn<FacilityGovernanceRole>({
      getActions: (row) => {
        const actions: Action[] = [];

        if (canManageGovernance && row.status === GovernanceStatus.ACTIVE) {
          actions.push({
            text: "Edit Role",
            section: "Manage",
            onClick: () => setRoleToEdit(row)
          });
        }

        if (canManageGovernance && row.status === GovernanceStatus.ARCHIVED) {
          actions.push({
            text: "Unarchive Role",
            section: "Manage",
            iconName: "unarchive",
            onClick: () => handleUnarchiveGovernanceRole(row)
          });
        }

        actions.push({
          text: "View Person",
          section: "Actions",
          onClick: () => {
            if (row.person.pgaId) {
              navigate(`/people/${row.person.pgaId}`);
            }
          }
        });

        if (canManageGovernance) {
          actions.push({
            text: "Add Note",
            section: "Actions",
            onClick: () => setRoleToAddNote(row)
          });
        }

        if (canManageGovernance && row.status === GovernanceStatus.ACTIVE) {
          actions.push({
            text: "Archive Role",
            iconName: "archive",
            destructive: true,
            onClick: () => setRoleToArchive(row)
          });
        }

        return actions;
      },
      onViewClick: (row) => {
        if (row.person.pgaId) {
          setSelectedPgaId(row.person.pgaId);
          setIsPersonSideSheetOpen(true);
        }
      },
      viewButtonTitle: "View person details",
      actionId,
      setMenu,
      getRowId: (row) => row.id
    })
  ];

  return (
    <>
      <Menu />
      <div className="pt-12" />
      <Table
        data={roles}
        loading={loading}
        hasActiveFilters={hasActiveFilters}
        getRowCanExpand={(row) => row.original.notes.length > 0}
        onClickRow={(row) => {
          const personPath = buildPersonPath(row.original.person.pgaId);
          if (personPath) {
            navigate(personPath);
          }
        }}
        columns={columns}
        renderControls={(_table, { onSortClick } = {}) => (
          <TableControls
            actions={
              canManageFacilities
                ? toTableControlActions([
                    {
                      label: "Add Governance Role",
                      icon: "add",
                      variant: "filled",
                      onClick: () => setIsAddGovernanceRoleModalOpen(true),
                      testId: "add-governance-role-button"
                    }
                  ])
                : []
            }
            filterChips={[
              {
                label: "Show Archived",
                onClick: () => setShowArchived(!showArchived),
                selected: showArchived
              }
            ]}
            activeFilters={activeFilterCount}
            onClearAllFilters={handleClearAllFilters}
            onSortClick={onSortClick}
          >
            <SearchInput
              data-name="search"
              placeholder="Search Governance Roles"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
            <GovernanceRoleFilter
              options={roleFilterOptions}
              selectedValues={selectedRoleIds}
              onSelect={onSelectRole}
              onClear={onClearRole}
            />
          </TableControls>
        )}
        renderExpandedRow={({ row }) => (
          <NotesTable
            variant="details-panel"
            data={row.original.notes}
            title={`Notes (${row.original.notes.length})`}
          />
        )}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Governance Roles"
            caption="There are no governance roles associated with this facility."
            iconName="groups"
          />
        )}
        renderFilteredEmptyState={() => (
          <EmptyState title="No Results Found" caption="Try a different search or filter" iconName="groups" />
        )}
        renderFooter={() => <TableFooter totalRowCount={roles.length} />}
      />
      <PersonSideSheet
        open={isPersonSideSheetOpen}
        onClose={() => {
          setIsPersonSideSheetOpen(false);
          setSelectedPgaId(undefined);
        }}
        pgaId={selectedPgaId ?? null}
        showEmployment={true}
        showFacility={true}
      />
      <EditGovernanceRoleModal
        open={roleToEdit !== null}
        role={roleToEdit}
        onClose={() => setRoleToEdit(null)}
        refetchQueries={[FACILITY_GOVERNANCE_ROLES_QUERY]}
      />
      {isAddGovernanceRoleModalOpen && (
        <AddGovernanceRoleModal
          open
          facilityId={facilityId}
          onClose={() => setIsAddGovernanceRoleModalOpen(false)}
          refetchQueries={[FACILITY_GOVERNANCE_ROLES_QUERY]}
        />
      )}
      <ArchiveGovernanceRoleDialog
        open={roleToArchive !== null}
        onClose={() => setRoleToArchive(null)}
        onConfirm={handleArchiveGovernanceRole}
        isLoading={archiving}
        personName={roleToArchive?.person.displayName}
        roleLabel={roleToArchive?.title.title}
      />
      {roleToAddNote && (
        <AddEditNoteSideSheet
          open
          onClose={() => setRoleToAddNote(null)}
          notableId={roleToAddNote.id}
          notableType={NoteNotableValues.FACILITY_ASSOCIATION}
          refetchQueries={[FACILITY_GOVERNANCE_ROLES_QUERY]}
          currentUserId={currentUserId || undefined}
          pgaId={roleToAddNote.person.pgaId ?? undefined}
          objectType="person"
          onSaveSuccess={() => setRoleToAddNote(null)}
        />
      )}
    </>
  );
}
